/**
 * use-knowledge-bases.ts — KHO TRI THỨC độc lập (mỗi kho một bộ Tài liệu / Hỏi đáp / Ảnh và video + cây danh mục riêng).
 * Backend: modules/knowledge/knowledge-base-routes.ts.
 * Khoá truy vấn bắt đầu bằng 'knowledge' → mọi thao tác thêm / sửa / xoá kiến thức (invalidate ['knowledge'])
 * tự làm mới số liệu kho.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

interface Envelope<T> { success: boolean; data: T }

export const KB_BASES_KEY = ['knowledge', 'bases'] as const

export interface KnowledgeBase {
  id: string
  name: string
  description: string | null
  /** Danh mục gốc cũ đã chuyển thành kho này (link cũ /ai?tab=knowledge&category=<id>). */
  legacyCategoryId?: string | null
  createdAt: string
  updatedAt: string
  /** Tài liệu = bài viết. */
  articleCount: number
  qaCount: number
  materialCount: number
  /** "Số nhân viên AI đang dùng" (chọn đích danh + không giới hạn kho). */
  botCount: number
  /** Nhân viên AI chọn đích danh kho này (chặn xoá). */
  bots: Array<{ id: string; name: string }>
  /** Nhân viên AI không giới hạn kho (dùng mọi kho). */
  allBasesBots: Array<{ id: string; name: string }>
}

export interface KnowledgeMaterial {
  id: string
  knowledgeBaseId: string
  categoryId: string | null
  kind: 'image' | 'video' | 'audio' | 'file'
  name: string
  description: string | null
  fileUrl: string
  mimeType: string | null
  fileSize: number | null
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export function useKnowledgeBases(q?: string) {
  return useQuery<KnowledgeBase[]>({
    queryKey: [...KB_BASES_KEY, q?.trim() || ''],
    queryFn: async () => {
      const { data } = await api.get<Envelope<KnowledgeBase[]>>('/knowledge-bases', { params: q?.trim() ? { q: q.trim() } : {} })
      return data.data
    },
    placeholderData: (prev) => prev,
    // Kho thường được tạo / sửa ở tab khác (màn nhân viên AI, luồng tạo mở Kho tri thức ở tab mới) → quay lại tab là cập nhật.
    refetchOnWindowFocus: true,
  })
}

export function useKnowledgeBase(id: string | undefined) {
  return useQuery<KnowledgeBase>({
    queryKey: [...KB_BASES_KEY, 'one', id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<Envelope<KnowledgeBase>>(`/knowledge-bases/${id}/summary`)
      return data.data
    },
  })
}

export function useCreateKnowledgeBase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; description?: string | null }) => {
      const { data } = await api.post<Envelope<KnowledgeBase>>('/knowledge-bases', input)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KB_BASES_KEY }),
  })
}

export function useUpdateKnowledgeBase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; name?: string; description?: string | null }) => {
      const { id, ...body } = vars
      const { data } = await api.patch<Envelope<KnowledgeBase>>(`/knowledge-bases/${id}`, body)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KB_BASES_KEY }),
  })
}

export function useDeleteKnowledgeBase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; mode?: 'move' | 'purge'; targetId?: string }) => {
      const { data } = await api.delete<Envelope<{ deleted: boolean }>>(`/knowledge-bases/${vars.id}`, {
        params: { ...(vars.mode ? { mode: vars.mode } : {}), ...(vars.targetId ? { targetId: vars.targetId } : {}) },
      })
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
      qc.invalidateQueries({ queryKey: ['knowledge-categories'] })
    },
  })
}

// ── Ảnh và video ──────────────────────────────────────────────────────────

export function useKnowledgeMaterials(baseId: string | undefined, params: { categoryId?: string; q?: string } = {}) {
  return useQuery<KnowledgeMaterial[]>({
    queryKey: ['knowledge', 'materials', baseId, params],
    enabled: !!baseId,
    queryFn: async () => {
      const { data } = await api.get<Envelope<KnowledgeMaterial[]>>(`/knowledge-bases/${baseId}/materials`, {
        params: { ...(params.categoryId ? { categoryId: params.categoryId } : {}), ...(params.q?.trim() ? { q: params.q.trim() } : {}) },
      })
      return data.data
    },
    placeholderData: (prev) => prev,
  })
}

export async function uploadKnowledgeMaterial(
  baseId: string,
  file: File,
  opts: { name?: string; description?: string; categoryId?: string | null; onProgress?: (pct: number) => void } = {},
): Promise<KnowledgeMaterial> {
  const form = new FormData()
  // Trường chữ TRƯỚC tệp: máy chủ đọc field theo thứ tự trong luồng multipart.
  if (opts.name) form.append('name', opts.name)
  if (opts.description) form.append('description', opts.description)
  if (opts.categoryId) form.append('categoryId', opts.categoryId)
  form.append('file', file)
  const { data } = await api.post<Envelope<KnowledgeMaterial>>(`/knowledge-bases/${baseId}/materials`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => { if (opts.onProgress && e.total) opts.onProgress(Math.round((e.loaded / e.total) * 100)) },
  })
  return data.data
}

export function useUpdateKnowledgeMaterial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; name?: string; description?: string | null; categoryId?: string | null; enabled?: boolean }) => {
      const { id, ...body } = vars
      const { data } = await api.patch<Envelope<KnowledgeMaterial>>(`/knowledge-materials/${id}`, body)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}

export function useDeleteKnowledgeMaterial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<Envelope<{ deleted: boolean }>>(`/knowledge-materials/${id}`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  })
}
